import {
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';

import { LabelBadge } from '@/components/Badge';
import { Icon } from '@/components/Icon/Icon';
import { IconName } from '@/components/Icon/Icon.interface';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectRightStartComingSoonFeature } from '@/redux/configs';
import { selectIsFeatureEnabled } from '@/redux/configs/baseSelectors';

import { FeatureName } from '@/types/app-configurations';
import { ApplicationType } from '@/types/user';

import { getDataTestId } from '@/utils/browser';

import { ActionButton, ListActionItemContainer } from './styled';

interface ApplicationTypeSelectionProps {
  onSelect: (type: ApplicationType, isComingSoon: boolean) => void;
  disabled?: boolean;
}

const ORDERED_APPLICATION_TYPES: {
  type: ApplicationType;
  helperText?: string;
}[] = [
  { type: ApplicationType.INDIVIDUAL },
  {
    type: ApplicationType.JOINT,
    helperText:
      'selectApplicationType.applicationTypeSelection.actionButton.JOINT.helperText',
  },
  { type: ApplicationType.OTHER },
  { type: ApplicationType.SHARED_WITH_SOMEONE_ELSE },
  { type: ApplicationType.BUSINESS },
];

const ApplicationTypeSelection: React.FC<ApplicationTypeSelectionProps> = ({
  onSelect,
  disabled,
}) => {
  const { t } = useTranslate();
  const onSelectApplication =
    (appType: ApplicationType, isComingSoon: boolean = false) =>
    () => {
      onSelect(appType, isComingSoon);
    };
  const showRightStartComingSoon = useAppSelector(
    selectRightStartComingSoonFeature,
  );
  const showBusinessComingSoon = useAppSelector(
    selectIsFeatureEnabled(FeatureName.BusinessComingSoon),
  );
  const showSharedWithSomeoneElseComingSoon = useAppSelector(
    selectIsFeatureEnabled(FeatureName.SharedWithSomeoneElseComingSoon),
  );

  const checkIfComingSoon = (appType: ApplicationType) => {
    if (appType === ApplicationType.BUSINESS) {
      return showBusinessComingSoon;
    }
    if (appType === ApplicationType.SHARED_WITH_SOMEONE_ELSE) {
      return showSharedWithSomeoneElseComingSoon;
    }
    if (appType === ApplicationType.JOINT) {
      return showRightStartComingSoon;
    }
    return false;
  };

  return (
    <List>
      {ORDERED_APPLICATION_TYPES.map(({ type: appType, helperText }) => {
        const isComingSoon = checkIfComingSoon(appType);
        return (
          <ListActionItemContainer
            key={appType}
            disabled={disabled}
            onClick={onSelectApplication(appType, isComingSoon)}
          >
            <ListItemButton>
              <ActionButton
                disabled={disabled}
                {...getDataTestId(`${appType}-action-button`)}
              >
                <Icon
                  name={getIconNameByApplicationType(appType)}
                  color={theme => theme.palette.primaries[400]}
                />
              </ActionButton>
            </ListItemButton>
            <ListItemText
              primary={
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  justifyContent="space-between"
                >
                  <Typography variant="body1" fontWeight="fontWeightMedium">
                    {t(
                      `selectApplicationType.applicationTypeSelection.actionButton.${appType}`,
                    )}
                  </Typography>
                  {isComingSoon && (
                    <LabelBadge
                      label={t(
                        'selectApplicationType.applicationTypeSelection.actionButton.coming_soon',
                      )}
                    />
                  )}
                </Stack>
              }
              secondary={
                helperText ? (
                  <Typography
                    variant="caption"
                    color={theme => theme.palette.text.secondary}
                  >
                    {t(helperText)}
                  </Typography>
                ) : undefined
              }
            />
          </ListActionItemContainer>
        );
      })}
    </List>
  );
};

function getIconNameByApplicationType(appType: ApplicationType): IconName {
  switch (appType) {
    case ApplicationType.INDIVIDUAL:
      return 'user';
    case ApplicationType.JOINT:
      return 'baby';
    case ApplicationType.BUSINESS:
      return 'suitcase';
    case ApplicationType.OTHER:
      return 'users';
    case ApplicationType.SHARED_WITH_SOMEONE_ELSE:
      return 'handshake';
    default:
      return 'user';
  }
}
export default ApplicationTypeSelection;
